/**
 * DataPreview — in-app table view of a node's parquet artifact.
 *
 * A parquet is binary: opening it in Jupyter's text editor fails with
 * "not UTF-8 encoded". This reads the artifact server-side (GET
 * .../nodes/{node_id}/artifact-preview) and renders schema + sample rows
 * as a table, so the user can SEE uploaded (INPUT_DATA) or produced
 * (OUTPUT) data without fighting Jupyter. "Open folder in Jupyter" stays
 * available for power use (drop more files / open the notebook) via the
 * renderJupyterView render-prop.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table2, FolderOpen, RefreshCw, AlertCircle, ArrowLeft,
  CheckCircle2, FileSpreadsheet, Loader2,
} from 'lucide-react';
import api, { type ArtifactPreview } from '../../apiService';
import { useProjectStore } from '../../stores/projectStore';
import { useProcessViewerStore } from '../../stores/processViewerStore';
import type { NodeDefinition } from '../../types/modeling';
import { getNodeTypeColor } from '../../types/modeling';
import { formatNumber } from '../../utils/plural';

interface UploadRecord {
  upload_id: string;
  filename?: string;
  row_count?: number;
  column_count?: number;
  uploaded_at?: string;
}

interface DataPreviewProps {
  node: NodeDefinition;
  /** Declared output to preview; omit to let the backend default to the
   *  node's first output (the common single-output case). */
  artifactName?: string;
  /** node_id whose current/ physically holds the data, when it differs from
   *  ``node``. OUTPUT nodes have no engine and write nothing — their data
   *  lives under the PRODUCER node that feeds them — so we read the producer's
   *  artifact while still presenting the output node (owner #3). Defaults to
   *  node.node_id. */
  sourceNodeId?: string;
  /** Read an immutable per-run snapshot instead of current/. */
  runId?: string | null;
  authToken?: string | null;
  /** Renders the Jupyter folder view when the user opts in. Keeps DataPreview
   *  decoupled from Jupyter token/embed plumbing. */
  renderJupyterView?: () => React.ReactNode;
}

/** Map a raw pandas dtype to a short, human label for the column subheader. */
function prettyDtype(dtype: string): string {
  const d = (dtype || '').toLowerCase();
  if (d.includes('datetime') || d.includes('timestamp')) return 'date';
  if (d.includes('bool')) return 'bool';
  if (d.includes('float') || d.includes('double')) return 'number';
  if (d.includes('int')) return 'integer';
  if (d === 'object' || d === 'str' || d.includes('string') || d.includes('category')) return 'text';
  return d || 'value';
}

export default function DataPreview({
  node,
  artifactName,
  sourceNodeId,
  runId,
  authToken,
  renderJupyterView,
}: DataPreviewProps) {
  const projectId = useProjectStore((s) => s.activeProject?.project_id);
  const modelId = useProcessViewerStore((s) => s.model?.model_id);
  const color = getNodeTypeColor(node.node_type);
  const isOutputNode = node.node_type === 'output';

  const [preview, setPreview] = useState<ArtifactPreview | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showJupyter, setShowJupyter] = useState(false);
  const [selectingUploadId, setSelectingUploadId] = useState<string | null>(null);

  // Uploaded-file history + active marker, surfaced inline so the user can see
  // WHICH file feeds the run/preview and switch to another (owner #2). Newest
  // first. Only meaningful for INPUT_DATA nodes; OUTPUT nodes have no uploads.
  const meta = (node.metadata || {}) as Record<string, any>;
  const activeUploadId: string | null =
    typeof meta.active_upload_id === 'string' ? meta.active_upload_id : null;
  const uploadHistory: UploadRecord[] = Array.isArray(meta.uploads)
    ? meta.uploads.slice().reverse()
    : [];
  const showFileList = !isOutputNode && uploadHistory.length > 0;

  const load = useCallback(async () => {
    if (!projectId || !modelId || !authToken) {
      setStatus('error');
      setErrorMsg('Missing session context.');
      return;
    }
    setStatus('loading');
    setErrorMsg(null);
    try {
      const fetchNodeId = sourceNodeId || node.node_id;
      const result = await api.getNodeArtifactPreview(projectId, modelId, fetchNodeId, authToken, {
        artifactName,
        runId: runId ?? undefined,
        maxRows: 50,
      });
      if (result === null) {
        setPreview(null);
        setStatus('empty');
      } else {
        setPreview(result);
        setStatus('ready');
      }
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message || 'Could not load the data preview.');
    }
  }, [projectId, modelId, node.node_id, sourceNodeId, artifactName, runId, authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelectUpload = useCallback(async (uploadId: string) => {
    if (!projectId || !modelId || !authToken) return;
    setSelectingUploadId(uploadId);
    try {
      const data = await api.selectNodeInputUpload(
        projectId, modelId, node.node_id, { upload_id: uploadId }, authToken,
      );
      // Patch the in-memory model so the active marker flips immediately
      // (the store-split: the upload store the FE reads doesn't auto-mirror).
      useProcessViewerStore.getState().setNodeActiveUpload(
        modelId, node.node_id, (data as any)?.active_upload_id ?? uploadId,
      );
      // Re-materialised current/ now holds the selected file — re-fetch.
      await load();
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message || 'Could not switch to that file.');
    } finally {
      setSelectingUploadId(null);
    }
  }, [projectId, modelId, node.node_id, authToken, load]);

  // Power-user path: full-height Jupyter folder view with a way back.
  if (showJupyter && renderJupyterView) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border-default bg-surface flex-shrink-0">
          <button
            onClick={() => setShowJupyter(false)}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-fg-secondary hover:bg-bg-warm transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to preview
          </button>
          <span className="text-[11px] text-fg-tertiary">Opened in Jupyter — parquet files won&apos;t render as text; use the preview to read data.</span>
        </div>
        <div className="flex-1 min-h-0">{renderJupyterView()}</div>
      </div>
    );
  }

  const emptyHint = isOutputNode
    ? 'Run the model to produce this output, then refresh to preview it.'
    : 'Upload a file in the Upload data tab, then refresh to preview it here.';

  return (
    <div className="h-full flex flex-col overflow-hidden bg-surface">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-default bg-surface flex-shrink-0">
        <span
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-body text-[10px] font-bold uppercase tracking-[0.08em] text-white flex-shrink-0"
          style={{ backgroundColor: color }}
        >
          <Table2 className="w-3 h-3" />
          {isOutputNode ? 'Output data' : 'Data'}
        </span>
        {preview && (
          <span className="font-mono text-[11px] font-semibold text-fg flex-shrink-0">
            {preview.artifact_name}
          </span>
        )}
        {preview && (
          <span className="text-[11px] text-fg-tertiary">
            {preview.column_count} cols · {formatNumber(preview.row_count)} rows
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={load}
            title="Refresh preview"
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-fg-secondary hover:bg-bg-warm transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          {renderJupyterView && (
            <button
              onClick={() => setShowJupyter(true)}
              title="Open the data folder in Jupyter"
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-fg-secondary hover:bg-bg-warm transition-colors"
            >
              <FolderOpen className="w-3.5 h-3.5" />
              Open folder in Jupyter
            </button>
          )}
        </div>
      </div>

      {/* Data files — list + active marker + switch (owner #2) */}
      {showFileList && (
        <div className="flex-shrink-0 border-b border-border-default bg-surface px-3 py-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-fg-tertiary">
              Data files ({uploadHistory.length})
            </span>
            <span className="text-[10px] text-fg-tertiary">
              The marked file feeds the run &amp; preview
            </span>
          </div>
          <ul className="space-y-1 max-h-28 overflow-auto">
            {uploadHistory.map((u) => {
              const isActive = u.upload_id === activeUploadId;
              const isSelecting = selectingUploadId === u.upload_id;
              return (
                <li
                  key={u.upload_id}
                  className={`flex items-center gap-2 rounded-md border px-2 py-1 ${
                    isActive ? 'border-success bg-success-bg' : 'border-border-default bg-bg-warm'
                  }`}
                >
                  <span className="flex-shrink-0">
                    {isActive ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                    ) : (
                      <FileSpreadsheet className="w-3.5 h-3.5 text-fg-tertiary" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-semibold text-fg">
                      {u.filename || u.upload_id}
                    </span>
                    <span className="text-[10px] text-fg-tertiary">
                      {formatNumber(u.row_count ?? 0)} rows · {u.column_count ?? 0} cols
                      {isActive ? ' · in use' : ''}
                    </span>
                  </div>
                  {!isActive && (
                    <button
                      type="button"
                      onClick={() => handleSelectUpload(u.upload_id)}
                      disabled={!!selectingUploadId}
                      className="inline-flex items-center gap-1 h-6 px-2 rounded text-[11px] font-semibold bg-surface text-fg-secondary border border-border-default hover:bg-bg-warm disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      {isSelecting ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                      Use
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto">
        {status === 'loading' && (
          <div className="h-full flex items-center justify-center" role="status" aria-live="polite">
            <div className="text-center">
              <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-fg-tertiary">Loading data…</p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="h-full flex items-center justify-center p-8">
            <div className="max-w-sm text-center">
              <AlertCircle className="w-8 h-8 text-danger mx-auto mb-2" />
              <p className="text-sm text-fg-secondary mb-3">{errorMsg}</p>
              <button
                onClick={load}
                className="px-3 py-1.5 text-xs font-medium bg-accent text-white rounded-md hover:bg-accent-hover transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {status === 'empty' && (
          <div className="h-full flex items-center justify-center p-8">
            <div className="max-w-sm text-center">
              <Table2 className="w-10 h-10 text-fg-tertiary mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-fg-secondary mb-1">No data yet</h3>
              <p className="text-xs text-fg-tertiary">{emptyHint}</p>
            </div>
          </div>
        )}

        {status === 'ready' && preview && (
          <table className="border-collapse text-xs">
            <thead className="sticky top-0 z-10">
              <tr className="bg-bg-warm">
                {preview.columns.map((col) => (
                  <th
                    key={col.name}
                    className="text-left font-semibold text-fg px-3 py-1.5 border-b border-r border-border-default whitespace-nowrap align-bottom"
                  >
                    <div className="leading-tight">{col.name}</div>
                    <div className="font-normal text-[10px] text-fg-tertiary lowercase tracking-wide">
                      {prettyDtype(col.dtype)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((row, rIdx) => (
                <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-surface' : 'bg-bg-warm'}>
                  {row.map((cell, cIdx) => (
                    <td
                      key={cIdx}
                      className="px-3 py-1 border-b border-r border-divider text-fg-secondary whitespace-nowrap font-mono"
                    >
                      {cell === null ? (
                        <span className="text-fg-tertiary italic">—</span>
                      ) : (
                        cell
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer — row truncation note */}
      {status === 'ready' && preview && preview.truncated && (
        <div className="flex-shrink-0 px-3 py-1.5 border-t border-border-default bg-bg-warm">
          <span className="text-[11px] text-fg-tertiary">
            Showing first {formatNumber(preview.preview_row_count)} of{' '}
            {formatNumber(preview.row_count)} rows.
          </span>
        </div>
      )}
    </div>
  );
}
